/**
 * Created by rastwe on 1/26/2017.
 */

import {HELIX_PROJECT_FIT, fileExtensionMapping} from './uiconstants';

import axios from 'axios';
import envConfig from '@ey/voltron-config';

class Env extends Object {
	/**
	 * Returns true if the environment is production.
	 * @return {boolean} is production
	 */
	static isProd() {
		const env = process.env || 'development';
		return env.NODE_ENV !== 'development';
	}

	/**
	 * Returns the current environment's server URL, appended with
	 * whatever path is passed in as an argument, or just the root
	 * serverURL if path is undefined.
	 * Notice that I'm requiring that all paths be preceeded by a "/"
	 * character. This is because you want any request to the API
	 * root to look like "/", instead of "".
	 * @param {String} path - Path to be appended to the server URL
	 * @return {String} complete path string
	 */
	static getServerUrl(path = '') {
		return `${envConfig.url.serviceUrl}${path}`;
	}

	static getWebUrl(path = '') {
		return `${envConfig.url.hercules_web}/${path}`;
	}

	static getBenefitServiceUrl() {
		return `${envConfig.url.benefitServiceUrl}`;
	}

	static getDocumentUrl(documentId, engagementId, parentName) {
		let path = `dashboard?category=documents&engagementId=${engagementId}`;
		if (documentId) {
			path += `&filterdocumentid=${documentId}`;
		}
		if (parentName) {
			path += `&view=${parentName}`;
		}

		const webUrl = `${this.getWebUrl(path + '&viewtype=documents#engagementOverview')}`;
		return webUrl;
	}

	static getAresUrl(configuration, engagementId, documentId, taskId, urlParameters) {
		if (configuration?.features && engagementId) {
			let url = configuration.features.canvasGuidedWorkflowBase;
			if (documentId) {
				// Activity page url
				url += url.endsWith('/') ? `${documentId}` : `/${documentId}`;
			}

			url += `?engagementId=${engagementId}`;
			if (taskId) {
				url += `&taskId=${taskId}`;
			}

			if (urlParameters) {
				Object.entries(urlParameters).forEach((urlParam) => {
					let [paramName, paramValue] = urlParam;
					url += `&${paramName}=${paramValue}`;
				});
			}

			url += url.includes('/fit?') ? '' : `&referrer=${encodeURI(new URL(window.location.href))}`;

			return url;
		}
	}

	static getFederatedHostName() {
		return FEDERATED_HOST_NAME;
	}

	/**
	 * DocHelperConfig is being updated to support Multiple Users
	 * Below is a ping call to check if docHelper API supports multiple users
	 * In case of failure it will fallback to exisiting api
	 * TODO: Remove the static method and update the config in take a parameter for user
	 * DO NOT use this method except for config
	 * All other data has to go through redux store.
	 */
	static docHelperConfig = envConfig.docHelper;

	static setDocHelperConfig = (u) => {
		const user = u.replace('@', '.');
		const updateConfig = (usr) => {
			this.docHelperConfig = {
				...this.docHelperConfig,
				docHelperApiUri: this.docHelperConfig.docHelperApiUri.replace(':58001', `:58001/${usr}`),
				SignalRHubUrl: this.docHelperConfig.SignalRHubUrl.replace(':58002', `:58002/${usr}`)
			};
		};
		const defaultDocHelperPing = axios({
			method: 'GET',
			url: `${this.docHelperConfig.docHelperApiUri}onlinestate`
		});

		let docHelperApi = {...this.docHelperConfig}.docHelperApiUri;
		docHelperApi = docHelperApi.replace(':58001', `:58001/${user}`);
		const userDocHelperPing = axios({
			method: 'GET',
			url: `${docHelperApi}onlinestate`
		});

		/**
		 * Result 1: default succeeded first - No change to config required
		 * Result 2: upn succeeded first - Update config with user upn
		 * Result 3: default failed first - Update config with user upn
		 * Result 4: upn failed first - No change to config required
		 */
		return Promise.race([defaultDocHelperPing, userDocHelperPing])
			.then((r) => {
				let config_url = r.config.url;
				if (config_url.includes(user)) {
					updateConfig(user);
				}
			})
			.catch((e) => {
				let config_url = e.config.url;
				if (!config_url.includes(user)) {
					updateConfig(user);
				}
			});
	};

	/**
	 * new method to get DochelperConfig. Application/feature code has to be use this method instead of _docHelperConfig
	 * _docHelper is a webpack define/injected variable build will replace it all over the code
	 */
	static getDocHelperConfig() {
		return this.docHelperConfig;
	}

	static getKronosUrl(path = '') {
		return `${_.trimEnd(envConfig.url.kronos_web, '/')}/${path}`;
	}

	static getSmartAutomationUrl(path = '') {
		return `${_.trimEnd(envConfig.url.Smart_Automation_Url, '/')}${path}`;
	}

	static getUniversalUrl(path = '') {
		return `${envConfig.url.universal_web}${path}`;
	}

	static getDocServiceUrl(path = '') {
		let url = envConfig.url.Engg_Doc_Url;

		if (url && url.endsWith('/')) {
			url = url.slice(0, -1);
		}

		if (path) {
			url += `/${path}`;
		}

		return url;
	}

	static getURL(urlName) {
		return envConfig.url[urlName];
	}

	static getInstrumentationKey() {
		return envConfig.ApplicationInsightsConfiguration.instrumentationkey;
	}

	/**
	 * Gets the base url for Hercules API calls.
	 * @param {string} urlName - urlName
	 * @param {number} engagementId - engagementId
	 * @return {String} the base URL, with the engagementId included in the url.
	 */
	static getBaseURL(urlName, engagementId) {
		let apiUrl = this.getURL(urlName);

		if (apiUrl && !apiUrl.endsWith('/')) {
			apiUrl += '/';
		}

		if (engagementId) {
			apiUrl = `${apiUrl}Engagements/${engagementId}/`;
		}

		return apiUrl;
	}

	/**
	 * Get the Language from the package.json file
	 * @return {String} the language defined in the package file
	 */
	static getLanguage() {
		return envConfig.language;
	}

	/**
	 * Gets the correct error message to be displayed
	 * @param {Object} error - the error
	 * @returns {String} the correct message
	 * */
	static getErrorMessage(error) {
		if (error.response) {
			if (error.response.data === '') {
				return error.response.statusText;
			} else if (error.response.data.errorMessage) {
				return error.response.data.errorMessage;
			}
		} else if (error.message && error.message !== '') {
			return error.message;
		} else {
			return error.response.message;
		}
	}

	/**
	 * Removes all html tags. Similar to getting innerText
	 * @param {string} text - the text to cleanup
	 * @returns {string} the cleaned up text
	 * */
	static removeAllHTMLTags(text) {
		return text.replace(/<(?:.|\n)*?>/gm, ' ');
	}

	static getTaskIdFromRoute(route) {
		let taskId = route && route.params && route.params.taskId;
		if (taskId) {
			return taskId;
		}
	}

	static getSelectedNoteFromRoute(route) {
		let note = {noteId: '', entityId: ''};
		note.noteId = route && route.params && route.params.noteId;
		note.entityId = route && route.params && route.params.entityid;
		return note;
	}

	static getEngagementIdFromRoute(route) {
		let engagementId = route && route.params && route.params.engagementId;
		if (engagementId) {
			return engagementId;
		}
	}

	/**
	 * renders text for tooltip. We may want to remove html tags later.
	 * @param {string} text - the text to cleanup
	 * @returns {string} the cleaned up text
	 * */
	static renderTextForTooltip(text) {
		/*if (text !== undefined && text.length > 0) {
			return text.replace(/<\/?(?!(img)\b)\w+[^>]*>|&nbsp;/gi, '');
		} else {*/
		return text;
		/*}*/
	}

	static getDocumentExtension(documentTypeId, fileExt, map) {
		// ToDo: Need to discuss and change accordingly

		if (fileExtensionMapping) {
			var mappedName = _.find(fileExtensionMapping.mapping, function(items) {
				return items.keys.indexOf(fileExt.toUpperCase()) >= 0;
			});

			if (mappedName) {
				return mappedName.mapIconName;
			}
		}

		return fileExt;
	}

	static allowDrydocks() {
		// Don't mind this expression.
		// It's just trying to work for string values in paas.json
		// and/or boolean values in development.json
		return envConfig.enableDrydocks === 'true' || envConfig.enableDrydocks === true;
	}

	static getHelixChartUrl(primaryHelixProject, analyticName) {
		let url = undefined;
		if (primaryHelixProject) {
			url = `${HELIX_PROJECT_FIT}${primaryHelixProject.projectId}/${primaryHelixProject.helixAnalyzerID}`;
			url += '/' + analyticName;
			url += this.getHelixChartAttributes(primaryHelixProject.helixProjectAttributesReadModel);
		}
		return url;
	}

	static getHelixChartAttributes(helixProjectAttributes) {
		let helixProjectsAttributesUrl = '';
		if (helixProjectAttributes) {
			helixProjectsAttributesUrl = `~&~CurCd=${helixProjectAttributes.currencyCode}~&~CurType=${helixProjectAttributes.currencyType}~&~AnalysYtd=${helixProjectAttributes.analysisPeriod?.yearToDateQuery}~&~CompYtd=${helixProjectAttributes.comparativePeriod?.yearToDateQuery}`;
			if (helixProjectAttributes.isAllBusinessUnitSelected === false && helixProjectAttributes?.businessUnit) {
				let businessUnit = '';
				if (helixProjectAttributes?.businessUnit && helixProjectAttributes?.businessUnit.length > 0) {
					businessUnit += '~&~BU=';
					helixProjectAttributes?.businessUnit.forEach((bu) => {
						businessUnit += bu + '~|~';
					});
				}
				if (businessUnit.length > 0 && businessUnit.charAt(businessUnit.length - 1) === '~') {
					businessUnit = businessUnit.substring(0, businessUnit.length - 3);
				}
				helixProjectsAttributesUrl += businessUnit;
			}
		}
		return helixProjectsAttributesUrl;
	}

	static getHelixViewChartUrl(clientAnalyticModel, analyticName) {
		let url = undefined;
		if (clientAnalyticModel) {
			url = `${HELIX_PROJECT_FIT}${clientAnalyticModel.projectId}/${clientAnalyticModel.analyzerId}`;
			url += '/' + analyticName;
			url += this.getHelixChartAttributes(clientAnalyticModel);
		}
		return url;
	}
}

export default Env;
